/**
 * districts.ts — plan intelligence: the deterministic FINALIZE pass every
 * settlement plan takes exactly once, plus pure side-table accessors.
 *
 * The Unbuilt Town wave (2026-08-31). Real-city rules applied to the raw
 * CityGenerator plan:
 *
 *  · SPACE SYNTAX (Hillier): street "integration" (closeness centrality over
 *    the road graph) decides land use — footfall-hungry functions (market,
 *    commerce, entertainment) move to the most integrated streets, homes to
 *    the quiet lanes. The generator's zoning assigned functions by band +
 *    position hash, never by network structure.
 *  · CRAFT QUARTERS: same-trade pads cluster along shared streets (the
 *    Shambles / Tanner Row pattern) via bounded deterministic function swaps;
 *    noxious trades (industry/manufacturing/utility) push to the rim.
 *  · PORT SPURS: a real road edge now connects the spaceport + shuttle pad to
 *    the network (recon 2026-08-31: no road reached any port). The spur joins
 *    the plan's road graph, so terrain stamping, the worn-track mask, road
 *    meshes and furniture all follow automatically.
 *  · THE PROCESSIONAL ROUTE: the ceremonial spine from the town's main entry
 *    to the plaza — parades, war marches and funerals stage along venues; now
 *    the route itself is a first-class, dressable object.
 *  · LANDMARK VIEW CORRIDOR: the approach wedge from the main entry to the
 *    core is height-capped so the crown tower reads from the arrival road
 *    (the "weenie" sightline rule).
 *
 * FINALIZE MUTATES ONLY JSON-SAFE PLAN DATA (functions, extra road edges) and
 * marks the plan `__atlasFinal` — a finalized plan that round-trips through
 * the bake JSON stays finalized and is never double-processed. Everything
 * else (names, routes, integration) is a lazy PURE accessor cached by
 * WeakMap, recomputable from a finalized plan on any thread.
 */
import type { CityPlan, RoadEdge } from 'enginex/city/config.js';
import type { BuildingFunction } from 'enginex/city/buildings/BuildingFunction.js';
import { terrainHeight } from '../../../shared/terrain.js';
import { settlementSites, type SettlementSite } from '../../../shared/settlements.js';
import { civilizationOf } from './biome_settlements.js';
import { FUNCTION_TO_TYPE } from 'enginex/city/buildings/BuildingFunction.js';
import { districtProgram, type DistrictProgram, type DistrictZoning } from '../../../shared/capitals.js';
import { capitalSkeletonFor, nearestSkeletonPoint, CONNECT_MAX_M } from './capital_skeleton.js';
import { spaceportSpot, shuttleSpot } from './ports.js';
import { townSlots, type TownSlot } from '../../../shared/town_layout.js';

/** The module's shared avalanche hash (numeric a,b) — one deterministic universe. */
export function h01(a: number, b: number): number {
  let s = (Math.imul(a >>> 0, 0x9e3779b1) ^ Math.imul((b >>> 0) + 1, 0x85ebca77)) >>> 0;
  s = Math.imul(s ^ (s >>> 15), 0x2c1b3c6d) >>> 0;
  s = Math.imul(s ^ (s >>> 13), 0x297a2d39) >>> 0;
  return ((s ^ (s >>> 15)) >>> 0) / 4294967296;
}

type MutPlan = CityPlan & { __atlasFinal?: true };

// ── Geometry helpers (local, pure) ──────────────────────────────────────────

function distToSeg(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const dx = bx - ax, dz = bz - az;
  const L2 = dx * dx + dz * dz;
  const t = L2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / L2)) : 0;
  return Math.hypot(px - (ax + dx * t), pz - (az + dz * t));
}

function distToPolyline(px: number, pz: number, poly: Float64Array): number {
  let best = Infinity;
  for (let i = 0; i + 3 < poly.length; i += 2) {
    best = Math.min(best, distToSeg(px, pz, poly[i], poly[i + 1], poly[i + 2], poly[i + 3]));
  }
  return best;
}

function signedDistToPolygon(px: number, pz: number, poly: Float64Array): number {
  let inside = false;
  let best = Infinity;
  const n = poly.length / 2;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[i * 2], zi = poly[i * 2 + 1];
    const xj = poly[j * 2], zj = poly[j * 2 + 1];
    const dx = xj - xi, dz = zj - zi;
    const L2 = dx * dx + dz * dz;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((px - xi) * dx + (pz - zi) * dz) / L2)) : 0;
    best = Math.min(best, Math.hypot(px - (xi + dx * t), pz - (zi + dz * t)));
    if ((zi > pz) !== (zj > pz) && px < ((xj - xi) * (pz - zi)) / (zj - zi + 1e-12) + xi) inside = !inside;
  }
  return inside ? -best : best;
}

function edgeLength(e: RoadEdge): number {
  let L = 0;
  const p = e.polyline;
  for (let i = 0; i + 3 < p.length; i += 2) L += Math.hypot(p[i + 2] - p[i], p[i + 3] - p[i + 1]);
  return L;
}

function padCentroid(f: Float64Array): [number, number] {
  let sx = 0, sz = 0;
  const n = f.length / 2;
  for (let i = 0; i < f.length; i += 2) { sx += f[i]; sz += f[i + 1]; }
  return [sx / n, sz / n];
}

// ── Space syntax: street integration (closeness centrality) ─────────────────

interface NetInfo {
  /** edge id → integration in [0,1] (1 = most integrated street in town). */
  integration: Map<number, number>;
  /** pad index → nearest edge id (frontage street). */
  padEdge: Int32Array;
  /** node id → summed shortest-path distance (raw closeness input). */
  nodeFar: Float64Array;
}

const netCache = new WeakMap<CityPlan, NetInfo>();

/** Closeness-centrality integration per street edge (space syntax). Pure. */
export function networkInfo(plan: CityPlan): NetInfo {
  const hit = netCache.get(plan);
  if (hit) return hit;
  const nodes = plan.roads.nodes;
  const edges = plan.roads.edges;
  const N = nodes.length;
  // Adjacency with edge lengths.
  const adj: Array<Array<{ to: number; w: number }>> = Array.from({ length: N }, () => []);
  for (const e of edges) {
    if (e.from >= N || e.to >= N) continue;
    const w = Math.max(1, edgeLength(e));
    adj[e.from].push({ to: e.to, w });
    adj[e.to].push({ to: e.from, w });
  }
  // All-pairs via Dijkstra from every node (binary-heap-free: plans carry a
  // few hundred nodes; an O(N² log N)-ish simple heap is fine at finalize).
  const nodeFar = new Float64Array(N);
  const dist = new Float64Array(N);
  for (let src = 0; src < N; src++) {
    dist.fill(Infinity);
    dist[src] = 0;
    // Small indexed heap (array of [d, node] kept lazily; pop-min by scan
    // would be O(N²·N) — use a simple binary heap inline).
    const heap: number[] = [src];
    const hd: number[] = [0];
    const pop = (): number => {
      let bi = 0;
      for (let i = 1; i < hd.length; i++) if (hd[i] < hd[bi]) bi = i;
      const n2 = heap[bi];
      heap.splice(bi, 1); hd.splice(bi, 1);
      return n2;
    };
    // Bounded frontier: grid road graphs have tiny frontiers; the scan-pop
    // stays cheap and allocation-free enough for a one-time pass.
    while (heap.length > 0) {
      const u = pop();
      const du = dist[u];
      for (const { to, w } of adj[u]) {
        const nd = du + w;
        if (nd < dist[to]) {
          dist[to] = nd;
          heap.push(to); hd.push(nd);
        }
      }
    }
    let sum = 0;
    let reach = 0;
    for (let i = 0; i < N; i++) if (Number.isFinite(dist[i]) && i !== src) { sum += dist[i]; reach++; }
    nodeFar[src] = reach > 0 ? sum / reach : Infinity;
  }
  // Edge integration = inverse mean farness of its endpoints, normalized.
  let lo = Infinity, hi = -Infinity;
  const raw = new Map<number, number>();
  for (const e of edges) {
    const f = ((Number.isFinite(nodeFar[e.from]) ? nodeFar[e.from] : 1e9)
      + (Number.isFinite(nodeFar[e.to]) ? nodeFar[e.to] : 1e9)) / 2;
    const v = 1 / Math.max(1, f);
    raw.set(e.id, v);
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  const integration = new Map<number, number>();
  for (const [id, v] of raw) integration.set(id, hi > lo ? (v - lo) / (hi - lo) : 0.5);
  // Pad → nearest edge (frontage street).
  const padEdge = new Int32Array(plan.buildings.length).fill(-1);
  for (let pi = 0; pi < plan.buildings.length; pi++) {
    const [cx, cz] = padCentroid(plan.buildings[pi].footprint);
    let bd = Infinity, be = -1;
    for (const e of edges) {
      const d = distToPolyline(cx, cz, e.polyline);
      if (d < bd) { bd = d; be = e.id; }
    }
    padEdge[pi] = be;
  }
  const info: NetInfo = { integration, padEdge, nodeFar };
  netCache.set(plan, info);
  return info;
}

// ── Function remap: space-syntax placement + craft quarters ─────────────────

/** How much a function wants footfall (retail migrates to integrated streets). */
const FOOTFALL: Record<string, number> = {
  market: 5, commerce: 4.5, entertainment: 4, recreation: 3.2, civic: 3,
  transit: 2.6, medical: 2.2, science: 1.8, command: 1.6, communication: 1.2,
  corporate: 1.0, agriculture: 0.6, storage: 0.3, habitation_luxury: 0.2,
  habitation: 0, manufacturing: -0.6, industry: -1.2, utility: -1.4,
};
const NOXIOUS = new Set(['industry', 'manufacturing', 'utility', 'storage']);
const CRAFT_CLASSES = ['commerce', 'manufacturing', 'industry', 'entertainment'] as const;

function remapFunctions(plan: MutPlan, siteSeed: number): void {
  const pads = plan.buildings;
  const net = networkInfo(plan);
  const R = Math.max(1, plan.config.radius);
  // Participants: pads with a defined function. The multiset of functions is
  // PRESERVED — only which pad holds which function changes, so the town's
  // overall composition (and every downstream count) stays what the zoning
  // policy produced.
  const idxs: number[] = [];
  for (let i = 0; i < pads.length; i++) if (pads[i].program.function) idxs.push(i);
  // Rank pads by (integration of frontage street, then inward): the best
  // shopping street first. Deterministic tiebreak by parcelId.
  const score = (i: number): number => {
    const integ = net.integration.get(net.padEdge[i]) ?? 0;
    const [cx, cz] = padCentroid(pads[i].footprint);
    const distFrac = Math.hypot(cx, cz) / R;
    return integ * 1.0 - distFrac * 0.15;
  };
  const byPlace = [...idxs].sort((a, b) => score(b) - score(a) || pads[a].parcelId - pads[b].parcelId);
  const fns = idxs.map((i) => pads[i].program.function as BuildingFunction);
  fns.sort((a, b) => (FOOTFALL[b] ?? 0) - (FOOTFALL[a] ?? 0) || (a < b ? -1 : 1));
  for (let k = 0; k < byPlace.length; k++) pads[byPlace[k]].program.function = fns[k];

  // Noxious rim rule: any noxious trade in the core swaps with the rim-most
  // dwelling — old towns pushed tanneries downwind by ordinance.
  const rimHomes = idxs
    .filter((i) => { const f = pads[i].program.function as string; return f === 'habitation' || f === 'habitation_luxury'; })
    .sort((a, b) => {
      const [ax, az] = padCentroid(pads[a].footprint);
      const [bx, bz] = padCentroid(pads[b].footprint);
      return Math.hypot(bx, bz) - Math.hypot(ax, az);
    });
  for (const i of idxs) {
    const f = pads[i].program.function as string;
    if (!NOXIOUS.has(f)) continue;
    const [cx, cz] = padCentroid(pads[i].footprint);
    if (Math.hypot(cx, cz) / R >= 0.55) continue;
    const home = rimHomes.shift();
    if (home === undefined) break;
    pads[i].program.function = pads[home].program.function;
    pads[home].program.function = f as BuildingFunction;
  }

  // Craft quarters: bounded SPATIAL clustering (the Shambles pattern). The
  // per-edge version failed in dense lattices — edges are short and front ≤2
  // pads each — so a quarter is a DISC: the class's anchor is its pad on the
  // most integrated street, and up to 5 far strays swap onto nearby hosts.
  const QUARTER_R = 70;
  for (const cls of CRAFT_CLASSES) {
    const clsPads = idxs.filter((i) => pads[i].program.function === cls);
    if (clsPads.length < 3) continue;
    let anchor = clsPads[0];
    let bestI = -Infinity;
    for (const i of clsPads) {
      const integ = net.integration.get(net.padEdge[i]) ?? 0;
      if (integ > bestI || (integ === bestI && pads[i].parcelId < pads[anchor].parcelId)) { bestI = integ; anchor = i; }
    }
    const [ax, az] = padCentroid(pads[anchor].footprint);
    const hosts = idxs
      .filter((i) => pads[i].program.function !== cls
        && (FOOTFALL[pads[i].program.function as string] ?? 0) <= (FOOTFALL[cls] ?? 0))
      .map((i) => { const [cx, cz] = padCentroid(pads[i].footprint); return { i, d: Math.hypot(cx - ax, cz - az) }; })
      .filter((h) => h.d < QUARTER_R)
      .sort((a, b) => a.d - b.d || a.i - b.i);
    const strays = clsPads
      .filter((i) => i !== anchor)
      .map((i) => { const [cx, cz] = padCentroid(pads[i].footprint); return { i, d: Math.hypot(cx - ax, cz - az) }; })
      .filter((st) => st.d > QUARTER_R)
      .sort((a, b) => b.d - a.d || a.i - b.i);
    const swaps = Math.min(hosts.length, strays.length, 5);
    for (let k = 0; k < swaps; k++) {
      const a = hosts[k].i, b = strays[k].i;
      const tmp = pads[a].program.function;
      pads[a].program.function = pads[b].program.function;
      pads[b].program.function = tmp;
    }
  }
  void siteSeed;
}

// ── Capital districts: program + skeleton frame ─────────────────────────────

/** The program of a district site (null for every other tier). */
export function districtProgramOf(site: SettlementSite, seed: string): DistrictProgram | null {
  if (site.tier !== 'district' || site.capitalId === undefined) return null;
  const cap = siteById(seed, site.capitalId);
  if (!cap) return null;
  return districtProgram(civilizationOf(cap.siteSeed), site.district ?? 0);
}

/** A district's capital frame: the skeleton plan + the district→capital local offset. */
function capitalFrame(site: SettlementSite, seed: string): { plan: CityPlan; ox: number; oz: number } | null {
  if (site.tier !== 'district' || site.capitalId === undefined) return null;
  const cap = siteById(seed, site.capitalId);
  return cap ? { plan: capitalSkeletonFor(cap, seed), ox: site.centerX - cap.centerX, oz: site.centerZ - cap.centerZ } : null;
}

/** Program → the function mix its pads are drawn from (shares sum to 1). */
const PROGRAM_MIX: Record<DistrictZoning, Array<[BuildingFunction, number]>> = {
  wards: [['habitation', 0.7], ['market', 0.1], ['civic', 0.05], ['entertainment', 0.05], ['recreation', 0.05], ['habitation_luxury', 0.05]],
  commerce: [['market', 0.4], ['commerce', 0.2], ['habitation', 0.15], ['entertainment', 0.1], ['storage', 0.1], ['civic', 0.05]],
  industry: [['manufacturing', 0.35], ['industry', 0.25], ['storage', 0.2], ['habitation', 0.1], ['utility', 0.1]],
  gardens: [['agriculture', 0.45], ['habitation', 0.3], ['storage', 0.15], ['market', 0.1]],
  civic: [['civic', 0.3], ['science', 0.3], ['habitation', 0.2], ['medical', 0.1], ['entertainment', 0.1]],
  mixed: [['habitation', 0.35], ['entertainment', 0.2], ['market', 0.15], ['recreation', 0.1], ['civic', 0.1], ['science', 0.1]],
};

/** Re-draw every functioned pad from the district program's mix (deterministic
 *  per parcel); the space-syntax remap then decides WHICH pad holds which —
 *  the multiset is the program's, the placement is the street network's. */
function programFunctions(plan: MutPlan, zoning: DistrictZoning, siteSeed: number): void {
  const mix = PROGRAM_MIX[zoning];
  for (const pad of plan.buildings) {
    if (!pad.program.function) continue;
    let r = h01(siteSeed, pad.parcelId * 11 + 5);
    let fn: BuildingFunction = mix[mix.length - 1][0];
    for (const [f, share] of mix) { if (r < share) { fn = f; break; } r -= share; }
    pad.program.function = fn;
    pad.program.type = FUNCTION_TO_TYPE[fn];
  }
}

// ── Port spur roads ─────────────────────────────────────────────────────────

/** Sampled, lightly smoothed grade profile (2 m step — RoadGrader parity). */
function spurProfile(seed: string, site: SettlementSite, ax: number, az: number, bx: number, bz: number): Float32Array {
  const L = Math.hypot(bx - ax, bz - az);
  const n = Math.max(2, Math.ceil(L / 2) + 1);
  const h = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    h[i] = terrainHeight(seed, site.centerX + ax + (bx - ax) * t, site.centerZ + az + (bz - az) * t);
  }
  for (let pass = 0; pass < 2; pass++) {
    for (let i = 1; i < n - 1; i++) h[i] = (h[i - 1] + h[i] * 2 + h[i + 1]) / 4;
  }
  return h;
}

/** A town gate: where a major street reaches the settlement's edge ring. */
export interface GateAnchor { x: number; z: number; ang: number; main: boolean }

/** Gate anchors stored on the plan by finalize (JSON-safe → bake-stable). */
export function gatesFor(plan: CityPlan): GateAnchor[] {
  return (plan as MutPlan & { __atlasGates?: GateAnchor[] }).__atlasGates ?? [];
}

/**
 * Pick the gates: rim road-ends of major streets, angularly separated. The
 * MAIN gate is the rim-most arterial end — the same node ceremonialRoute
 * elects as the town's entry, so the parade route runs in the main gate.
 * Each gate street is EXTENDED through the wall line (a real exit road), so
 * stamping, the worn track and the road mesh all carry out the gate.
 */
function addGates(plan: MutPlan, site: SettlementSite, seed: string): void {
  const nodes = plan.roads.nodes as Array<{ id: number; x: number; z: number }>;
  const edges = plan.roads.edges as RoadEdge[];
  if (nodes.length === 0 || edges.length === 0) return;
  const deg = new Map<number, number>();
  const bestClass = new Map<number, RoadEdge>();
  const RANK: Record<RoadEdge['roadClass'], number> = { arterial: 2, collector: 1, local: 0 };
  for (const e of edges) {
    for (const id of [e.from, e.to]) {
      deg.set(id, (deg.get(id) ?? 0) + 1);
      const cur = bestClass.get(id);
      if (!cur || RANK[e.roadClass] > RANK[cur.roadClass]) bestClass.set(id, e);
    }
  }
  const R = Math.max(1, plan.config.radius);
  const cand = nodes
    .filter((n) => (deg.get(n.id) ?? 0) <= 2 && Math.hypot(n.x, n.z) > R * 0.55 && (RANK[bestClass.get(n.id)?.roadClass ?? 'local'] >= 1))
    .sort((a, b) => (RANK[bestClass.get(b.id)!.roadClass] - RANK[bestClass.get(a.id)!.roadClass])
      || (Math.hypot(b.x, b.z) - Math.hypot(a.x, a.z)) || a.id - b.id);
  const maxGates = site.tier === 'city' || site.tier === 'district' ? 4 : site.tier === 'town' || site.tier === 'circuit' ? 3 : 2;
  const gates: GateAnchor[] = [];
  const wallR = R + 8;
  // Capital wave (phase 2): a DISTRICT's exits run to the nearest skeleton road
  // (ring road or boulevard) instead of a wall line — the capital's fabric, not a
  // walled town. A rim node facing a gap with no road within reach is not a gate.
  const skel = capitalFrame(site, seed);
  for (const n of cand) {
    if (gates.length >= maxGates) break;
    const ang = Math.atan2(n.z, n.x);
    let clash = false;
    for (const g of gates) {
      let d = Math.abs(ang - g.ang) % (Math.PI * 2);
      if (d > Math.PI) d = Math.PI * 2 - d;
      if (d < 0.85) { clash = true; break; }
    }
    if (clash) continue;
    let gx = Math.cos(ang) * wallR;
    let gz = Math.sin(ang) * wallR;
    let outX = Math.cos(ang) * (wallR + 14);
    let outZ = Math.sin(ang) * (wallR + 14);
    if (skel) {
      const m = nearestSkeletonPoint(skel.plan, n.x + skel.ox, n.z + skel.oz);
      if (!m || m.d > CONNECT_MAX_M) continue;
      outX = m.x - skel.ox; outZ = m.z - skel.oz; // the gate anchor IS the meet point
      gx = outX; gz = outZ;
    }
    gates.push({ x: gx, z: gz, ang, main: gates.length === 0 });
    // The exit road: node → just beyond the wall line (a town) or onto the
    // skeleton (a district), in the street's class.
    const parent = bestClass.get(n.id)!;
    let maxId = -1;
    for (const e of edges) if (e.id > maxId) maxId = e.id;
    const gateNode = { id: nodes.length, x: outX, z: outZ };
    nodes.push(gateNode);
    edges.push({
      id: maxId + 1,
      from: n.id,
      to: gateNode.id,
      polyline: Float64Array.from([n.x, n.z, outX, outZ]),
      roadClass: parent.roadClass,
      halfWidth: parent.halfWidth,
      gradeProfile: spurProfile(seed, site, n.x, n.z, outX, outZ),
    } as RoadEdge);
  }
  (plan as MutPlan & { __atlasGates?: GateAnchor[] }).__atlasGates = gates;
}

function addSpur(plan: MutPlan, site: SettlementSite, seed: string, sx: number, sz: number): void {
  const nodes = plan.roads.nodes as Array<{ id: number; x: number; z: number }>;
  const edges = plan.roads.edges as RoadEdge[];
  if (nodes.length === 0) return;
  // Candidate join nodes: the 6 nearest; take the first whose straight spur
  // stays out of every pad footprint (sampled), else the least-intruding.
  const cand = nodes
    .map((n) => ({ n, d: Math.hypot(n.x - sx, n.z - sz) }))
    .sort((a, b) => a.d - b.d || a.n.id - b.n.id)
    .slice(0, 6);
  let best: { n: { id: number; x: number; z: number }; intr: number } | null = null;
  for (const { n } of cand) {
    const L = Math.hypot(n.x - sx, n.z - sz);
    if (L < 4) { best = { n, intr: 0 }; break; }
    let intr = 0;
    const steps = Math.max(2, Math.ceil(L / 4));
    for (let k = 1; k < steps; k++) {
      const t = k / steps;
      const px = sx + (n.x - sx) * t, pz = sz + (n.z - sz) * t;
      for (const pad of plan.buildings) {
        const sd = signedDistToPolygon(px, pz, pad.footprint);
        if (sd < 1.5) intr = Math.max(intr, 1.5 - sd);
      }
    }
    if (intr === 0) { best = { n, intr }; break; }
    if (!best || intr < best.intr) best = { n, intr };
  }
  if (!best) return;
  let maxId = -1;
  for (const e of edges) if (e.id > maxId) maxId = e.id;
  const spotNode = { id: nodes.length, x: sx, z: sz };
  nodes.push(spotNode);
  const e: RoadEdge = {
    id: maxId + 1,
    from: best.n.id,
    to: spotNode.id,
    polyline: Float64Array.from([best.n.x, best.n.z, sx, sz]),
    roadClass: 'local',
    halfWidth: 3,
    gradeProfile: spurProfile(seed, site, best.n.x, best.n.z, sx, sz),
  } as RoadEdge;
  edges.push(e);
}

// ── The finalize pass ───────────────────────────────────────────────────────

const siteListCache = new Map<string, SettlementSite[]>();
function siteBySeed(seed: string, siteSeed: number): SettlementSite | null {
  let list = siteListCache.get(seed);
  if (!list) { list = settlementSites(seed); siteListCache.set(seed, list); }
  return list.find((s) => s.siteSeed === siteSeed) ?? null;
}
function siteById(seed: string, id: number): SettlementSite | null {
  let list = siteListCache.get(seed);
  if (!list) { list = settlementSites(seed); siteListCache.set(seed, list); }
  return list.find((s) => s.id === id) ?? null;
}

/**
 * Sitte's room: carve open ground for the civic venues out of the building
 * fabric. The venue slots are where the server stages every election, feast,
 * trial and parade — dense archetypes packed pads right onto them (buried
 * stages, no ground for the plaza ring or the bazaar). Pads intersecting a
 * venue's clearing are dropped BEFORE anything else reads the plan, so
 * stamping, clearance, furniture and NPC projection all agree the square is
 * open. Radii by role: the plaza is a real square, the market a working
 * yard, the other venues a modest clearing.
 */
function carveVenueRooms(plan: MutPlan, site: SettlementSite, seed: string): void {
  let slots: TownSlot[];
  try { slots = townSlots(seed, site); } catch { return; }
  const plazaR = site.tier === 'city' || site.tier === 'district' ? 27 : site.tier === 'town' || site.tier === 'circuit' ? 24 : 20;
  const discs: Array<{ x: number; z: number; r: number }> = [];
  for (const slot of slots) {
    const lx = slot.x - site.centerX, lz = slot.z - site.centerZ;
    const r = slot.key === 'plaza' ? plazaR : slot.key === 'market' ? 11 : 7;
    discs.push({ x: lx, z: lz, r });
  }
  const pads = plan.buildings as CityPlan['buildings'];
  for (let i = pads.length - 1; i >= 0; i--) {
    const [cx, cz] = padCentroid(pads[i].footprint);
    for (const d of discs) {
      if (signedDistToPolygon(d.x, d.z, pads[i].footprint) < d.r
        || Math.hypot(cx - d.x, cz - d.z) < d.r) { pads.splice(i, 1); break; }
    }
  }
}

/**
 * Finalize a settlement plan IN PLACE (idempotent — a baked, already-final
 * plan round-trips untouched). Runs at every plan cache-write choke point, so
 * main thread, both workers, bakers and verify all see the same plan.
 */
export function finalizeSettlementPlan(plan: CityPlan, seed: string, siteSeed: number): CityPlan {
  const p = plan as MutPlan;
  if (p.__atlasFinal) return plan;
  const site = siteBySeed(seed, siteSeed);
  if (!site) { p.__atlasFinal = true; return plan; }
  try {
    carveVenueRooms(p, site, seed);
    // A capital district's pads are drawn from its PROGRAM first (phase 2).
    const prog = districtProgramOf(site, seed);
    if (prog) programFunctions(p, prog.zoning, siteSeed);
    remapFunctions(p, siteSeed);
    // Port spurs: the road network finally reaches the ports (never a district's — its ports are the capital's).
    if (site.tier !== 'district') {
      const sp = spaceportSpot(seed, site);
      if (sp) addSpur(p, site, seed, sp.x, sp.z);
      const sh = shuttleSpot(seed, site);
      if (sh) addSpur(p, site, seed, sh.x, sh.z);
    }
    // Gates: major streets exit through the town's edge ring.
    addGates(p, site, seed);
  } catch { /* a malformed plan finalizes as-is — never block settlement build */ }
  p.__atlasFinal = true;
  return plan;
}

// ── Lazy pure side-tables (recomputable from a finalized plan) ──────────────

/** Transect band by distance fraction from the core (render-time grain). */
export function transectBand(distFrac: number): 'core' | 'mid' | 'edge' {
  return distFrac < 0.35 ? 'core' : distFrac < 0.72 ? 'mid' : 'edge';
}

export interface CeremonialRoute {
  /** Edge ids of the processional spine, main entry → plaza. */
  edgeIds: Set<number>;
  /** The main-entry node (the town's front door — the gate anchor). */
  entry: { x: number; z: number };
  /** Bearing from the core toward the entry (the approach direction). */
  entryAngle: number;
  /** Ordered node positions along the route (entry → core). */
  path: Array<{ x: number; z: number }>;
}

const routeCache = new WeakMap<CityPlan, CeremonialRoute | null>();

/**
 * The processional route: from the rim-most arterial node (the main entry)
 * to the node nearest the core, preferring big streets. Pure + cached.
 */
export function ceremonialRoute(plan: CityPlan, siteSeed: number): CeremonialRoute | null {
  if (routeCache.has(plan)) return routeCache.get(plan) ?? null;
  const nodes = plan.roads.nodes;
  const edges = plan.roads.edges;
  let out: CeremonialRoute | null = null;
  if (nodes.length > 1 && edges.length > 0) {
    // Entry: the arterial-touching node farthest from the core (falls back to
    // any node when the plan has no arterials — hamlets).
    const touchesArterial = new Set<number>();
    for (const e of edges) if (e.roadClass === 'arterial') { touchesArterial.add(e.from); touchesArterial.add(e.to); }
    const pool = touchesArterial.size > 0 ? [...touchesArterial] : nodes.map((n) => n.id);
    let entryId = pool[0];
    let entryD = -1;
    for (const id of pool) {
      const n = nodes[id];
      if (!n) continue;
      const d = Math.hypot(n.x, n.z);
      if (d > entryD) { entryD = d; entryId = id; }
    }
    // Core: the node nearest (0,0).
    let coreId = nodes[0].id;
    let coreD = Infinity;
    for (const n of nodes) {
      const d = Math.hypot(n.x, n.z);
      if (d < coreD) { coreD = d; coreId = n.id; }
    }
    // Dijkstra entry → core, class-weighted (parades keep to the big streets).
    const N = nodes.length;
    const adj: Array<Array<{ to: number; w: number; eid: number }>> = Array.from({ length: N }, () => []);
    const classW: Record<RoadEdge['roadClass'], number> = { arterial: 1, collector: 1.6, local: 2.4 };
    for (const e of edges) {
      if (e.from >= N || e.to >= N) continue;
      const w = Math.max(1, edgeLength(e)) * classW[e.roadClass];
      adj[e.from].push({ to: e.to, w, eid: e.id });
      adj[e.to].push({ to: e.from, w, eid: e.id });
    }
    const dist = new Float64Array(N).fill(Infinity);
    const prevE = new Int32Array(N).fill(-1);
    const prevN = new Int32Array(N).fill(-1);
    dist[entryId] = 0;
    const heap: number[] = [entryId];
    const hd: number[] = [0];
    while (heap.length > 0) {
      let bi = 0;
      for (let i = 1; i < hd.length; i++) if (hd[i] < hd[bi]) bi = i;
      const u = heap[bi];
      heap.splice(bi, 1); hd.splice(bi, 1);
      for (const { to, w, eid } of adj[u]) {
        const nd = dist[u] + w;
        if (nd < dist[to]) {
          dist[to] = nd;
          prevE[to] = eid;
          prevN[to] = u;
          heap.push(to); hd.push(nd);
        }
      }
    }
    if (Number.isFinite(dist[coreId])) {
      const edgeIds = new Set<number>();
      const path: Array<{ x: number; z: number }> = [];
      let cur = coreId;
      let guard = 0;
      while (cur !== entryId && cur >= 0 && guard++ < N + 2) {
        if (prevE[cur] >= 0) edgeIds.add(prevE[cur]);
        const n = nodes[cur];
        if (n) path.push({ x: n.x, z: n.z });
        cur = prevN[cur];
      }
      const en = nodes[entryId];
      if (en) path.push({ x: en.x, z: en.z });
      path.reverse(); // entry → core
      if (edgeIds.size > 0 && en) {
        out = { edgeIds, entry: { x: en.x, z: en.z }, entryAngle: Math.atan2(en.z, en.x), path };
      }
    }
  }
  void siteSeed;
  routeCache.set(plan, out);
  return out;
}

/**
 * Landmark view-corridor cap: inside the wedge from the main entry toward the
 * core, building target heights are capped so the crown reads from the
 * approach. Returns a multiplier (1 = uncapped).
 */
export function corridorHeightCap(plan: CityPlan, siteSeed: number, lx: number, lz: number): number {
  const route = ceremonialRoute(plan, siteSeed);
  if (!route) return 1;
  const d = Math.hypot(lx, lz);
  const R = Math.max(1, plan.config.radius);
  if (d < R * 0.3) return 1; // the core itself is the skyline
  let ang = Math.atan2(lz, lx) - route.entryAngle;
  while (ang > Math.PI) ang -= Math.PI * 2;
  while (ang < -Math.PI) ang += Math.PI * 2;
  const HALF_WEDGE = 0.30; // ~17° each side of the approach axis
  if (Math.abs(ang) > HALF_WEDGE) return 1;
  // Deep in the wedge: strongest cap near the rim (the far approach), easing
  // toward the core so the skyline still steps up.
  const t = Math.min(1, (d / R - 0.3) / 0.7);
  return 1 - 0.45 * t * (1 - Math.abs(ang) / HALF_WEDGE);
}

// ── Desire lines (the ground remembers where people actually walk) ──────────

const desireCache = new WeakMap<CityPlan, Float64Array[]>();

/**
 * Deterministic desire lines: the walked connections between the venues the
 * server actually routes daily life through (plaza ↔ market/tavern/well/
 * shrine/stage, market ↔ tavern), deflected out of building pads by an
 * elastic-band pass. Folded into the worn-track mask at reduced weight so a
 * faint dirt line crosses the green exactly where the errands run.
 * LOCAL plan coords; pure + cached per plan.
 */
export function desireLinesFor(plan: CityPlan, seed: string, siteSeed: number): Float64Array[] {
  const hit = desireCache.get(plan);
  if (hit) return hit;
  const out: Float64Array[] = [];
  const site = siteBySeed(seed, siteSeed);
  if (site) {
    let slots: TownSlot[] = [];
    try { slots = townSlots(seed, site); } catch { /* siteless */ }
    const local = new Map<string, { x: number; z: number }>();
    for (const s of slots) local.set(s.key, { x: s.x - site.centerX, z: s.z - site.centerZ });
    const pairs: Array<[string, string]> = [
      ['plaza', 'market'], ['plaza', 'tavern'], ['plaza', 'well'],
      ['plaza', 'shrine'], ['plaza', 'stage'], ['market', 'tavern'],
    ];
    for (const [ka, kb] of pairs) {
      const a = local.get(ka);
      const b = local.get(kb);
      if (!a || !b) continue;
      const L = Math.hypot(b.x - a.x, b.z - a.z);
      if (L < 14) continue;
      const n = Math.max(4, Math.min(24, Math.round(L / 8)));
      const xs = new Float64Array((n + 1) * 2);
      for (let i = 0; i <= n; i++) {
        const t = i / n;
        xs[i * 2] = a.x + (b.x - a.x) * t;
        xs[i * 2 + 1] = a.z + (b.z - a.z) * t;
      }
      // Elastic band: interior points push out of pads, then relax smooth.
      for (let pass = 0; pass < 8; pass++) {
        let moved = false;
        for (let i = 1; i < n; i++) {
          const px = xs[i * 2], pz = xs[i * 2 + 1];
          for (const pad of plan.buildings) {
            const sd = signedDistToPolygon(px, pz, pad.footprint);
            if (sd < 1.6) {
              const e = 0.5;
              const gx = signedDistToPolygon(px + e, pz, pad.footprint) - signedDistToPolygon(px - e, pz, pad.footprint);
              const gz = signedDistToPolygon(px, pz + e, pad.footprint) - signedDistToPolygon(px, pz - e, pad.footprint);
              const gl = Math.hypot(gx, gz) || 1;
              xs[i * 2] += (gx / gl) * (1.6 - sd);
              xs[i * 2 + 1] += (gz / gl) * (1.6 - sd);
              moved = true;
              break;
            }
          }
        }
        for (let i = 1; i < n; i++) {
          xs[i * 2] = xs[i * 2] * 0.6 + (xs[(i - 1) * 2] + xs[(i + 1) * 2]) * 0.2;
          xs[i * 2 + 1] = xs[i * 2 + 1] * 0.6 + (xs[(i - 1) * 2 + 1] + xs[(i + 1) * 2 + 1]) * 0.2;
        }
        if (!moved && pass >= 3) break;
      }
      out.push(xs);
    }
  }
  desireCache.set(plan, out);
  return out;
}

// ── Street names + quarters (display layer) ─────────────────────────────────

const STREET_KIND_NAMES: Record<string, [string, string]> = {
  // fn → [Aurelian voice, Borean voice]
  market: ['Gilded Row', 'Trade Row'],
  commerce: ['Merchants’ Way', 'Barter Street'],
  entertainment: ['Revel Street', 'Song Street'],
  manufacturing: ['Smiths’ Row', 'Forge Lane'],
  industry: ['Ore Way', 'Bore Street'],
  civic: ['Concord Way', 'Compact Street'],
  habitation: ['Hearth Lane', 'Home Reach'],
  habitation_luxury: ['High Terrace', 'Frost Terrace'],
  science: ['Scholars’ Walk', 'Chart Walk'],
  agriculture: ['Granary Way', 'Provision Lane'],
};

export interface StreetName { edgeId: number; name: string; fn: string }

const streetCache = new WeakMap<CityPlan, Map<number, StreetName>>();

/** Deterministic street names: an edge is named for its dominant frontage
 *  trade, in the settlement civilization's voice. Only edges with ≥2 fronting
 *  pads get names (the ones a fingerpost would mention). Pure + cached. */
export function streetNames(plan: CityPlan, siteSeed: number): Map<number, StreetName> {
  const hit = streetCache.get(plan);
  if (hit) return hit;
  const net = networkInfo(plan);
  const civ = civilizationOf(siteSeed);
  const byEdge = new Map<number, Map<string, number>>();
  for (let i = 0; i < plan.buildings.length; i++) {
    const fn = plan.buildings[i].program.function as string | undefined;
    if (!fn) continue;
    const e = net.padEdge[i];
    if (e < 0) continue;
    let m = byEdge.get(e);
    if (!m) { m = new Map(); byEdge.set(e, m); }
    m.set(fn, (m.get(fn) ?? 0) + 1);
  }
  const out = new Map<number, StreetName>();
  const ORDINALS = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Old', 'New', 'Long', 'Short'];
  let ordinal = 0;
  for (const [eid, m] of byEdge) {
    let total = 0;
    let bestFn = '';
    let bestN = 0;
    for (const [fn, n] of m) { total += n; if (n > bestN) { bestN = n; bestFn = fn; } }
    if (total < 2) continue;
    const kind = STREET_KIND_NAMES[bestFn];
    let name: string;
    if (kind && bestN * 2 >= total) {
      name = kind[civ === 'a' ? 0 : 1];
    } else {
      name = `${ORDINALS[ordinal % ORDINALS.length]} Street`;
      ordinal++;
    }
    out.set(eid, { edgeId: eid, name, fn: bestFn });
  }
  // Disambiguate duplicates ("Smiths' Row" twice → "Upper/Lower …").
  const seen = new Map<string, number>();
  for (const sn of out.values()) {
    const n = (seen.get(sn.name) ?? 0) + 1;
    seen.set(sn.name, n);
    if (n === 2) sn.name = `Upper ${sn.name}`;
    else if (n > 2) sn.name = `Lower ${sn.name}`;
  }
  return (streetCache.set(plan, out), out);
}
